import { BaseAdapter } from './BaseAdapter'
import { BackendProvider } from './types'
import {
	PlaceOrModifyOrdersRequest,
	PlaceOrModifyOrdersResponse,
	GetAllOrdersRequest,
	CancelOrdersRequest,
	CancelOrdersResponse,
	Holding,
	TradingAccountMargin,
	GetTradingAccountMarginRequest
} from '../proto/generated/trading-bridge'
import { Broker_OrderType, Broker_Variety, MarginFromBrokerRequest, MarginFromBrokerResponse, Order, OrderStatus, OrderType, Product, productToJSON, TransactionType, Validity } from '../proto/generated/order'
import { Position, Position_Status, PositionRequest, PositionResponse } from '../proto/generated/position'
import api, { thirdPartyApi, rawApi } from '../services/apis/axiosClient'
import { ACCESS_TOKEN, PROVIDER_TOKEN } from '../assets/constants'
import { clearAuthSession } from '../services/auth/tokenRefresh'
import store from '../store'
import { Exchange } from '../proto/generated/common'
import { OptionType, Security, SecurityCategory_CategoryCode, Segment } from '../proto/generated/security'
import {
	ShoonyaPlaceOrderRequest,
	ShoonyaModifyOrderRequest,
	ShoonyaPlaceOrderResponse,
	ShoonyaModifyOrderResponse,
	ShoonyaCancelOrderRequest,
	ShoonyaCancelOrderResponse,
	ShoonyaOrderBookRequest,
	ShoonyaOrderBookResponse,
	ShoonyaOrderBookItem,
	ShoonyaPositionBookRequest,
	ShoonyaPositionBookResponse,
	ShoonyaPositionBookItem,
	ShoonyaHoldingsRequest,
	ShoonyaHoldingsResponse,
	ShoonyaHoldingItem,
	ShoonyaLogoutRequest,
	ShoonyaLogoutResponse,
	ShoonyaLimitsRequest,
	ShoonyaLimitsResponse,
	ShoonyaOrderMarginRequest,
	ShoonyaOrderMarginResponse,
	ShoonyaApiPayload
} from './shoonya.types'
import { getSecurityAsync, getSecurityKey } from '../util/security'
import { v4 as uuid } from 'uuid'
import { epochToDDMMMYY } from '../util/dateTime'
import { AuthProviderLoginUrlResponse, LoginResponse, LoginRequest, LoginWithMobileRequest, LoginWithMobileResponse, UserSignUpRequest, OperationResponse, GetActiveSessionsResponse, ValidateThirdPartyTokenRequest, ForgetPasswordRequest, User, VerifyMobileLoginRequest } from '../proto/generated/identity'

export class ShoonyaAdapter extends BaseAdapter {
	ourSecurityToShoonyaSecurity: Map<string, {
		tsym: string
		exch: 'NSE' | 'NFO' | 'BSE' | 'BFO' | 'MCX'
	}> = new Map()
	shoonyaSecurityToOurSecurity: Map<string, Security> = new Map()

	genrateLocalSecurityMap(security: Security): void {
		if (!security || !security?.code || !security?.exchange) {
			return
		}
		const ourKey = getSecurityKey(security.exchange, security.code)
		const { tsym, exch } = this.toShoonyaSecurity(security)
		const shoonyaKey = `${exch}:${tsym}`

		this.ourSecurityToShoonyaSecurity.set(ourKey, { tsym, exch })
		this.shoonyaSecurityToOurSecurity.set(shoonyaKey, security)
	}

	transformRequest<T = any>(
		endpoint: string,
		data: T,
		method: string
	): {
		url: string
		data: any
		headers?: Record<string, string>
	} {
		return {
			url: endpoint,
			data: data,
			headers: this.getCustomHeaders()
		}
	}

	transformResponse<T = any>(response: any, _endpoint: string): T {
		return response as T
	}

	transformError(error: any): any {
		const baseError = super.transformError(error)
		return {
			...baseError,
			provider: BackendProvider.SHOONYA,
			originalError: error
		}
	}

	private getAuthHeaders() {
		return {
			'Content-Type': 'application/json',
			'Accept': 'application/json'
		}
	}

	private toShoonyaExchange(exchange: Exchange, segment?: Segment): 'NSE' | 'NFO' | 'BSE' | 'BFO' | 'MCX' {
		switch (exchange) {
			case Exchange.NSE:
				switch (segment) {
					case Segment.CM:
						return 'NSE'
					case Segment.FO:
						return 'NFO'
					default:
						return 'NSE'
				}
			case Exchange.BSE:
				switch (segment) {
					case Segment.CM:
						return 'BSE'
					case Segment.FO:
						return 'BFO'
					default:
						return 'BSE'
				}
			case Exchange.MCX:
				return 'MCX'
			default:
				throw new Error(`Unsupported exchange: ${exchange}`)
		}
	}

	private toShoonyaSecurity(security: Security): { tsym: string; exch: 'NSE' | 'NFO' | 'BSE' | 'BFO' | 'MCX' } {
		const { exchange, code, segment, series, name, exchangeSymbol, categoryCode, optionType, expiry, strikePrice, priceMultiplier = 100} = security
		const ourKey = getSecurityKey(exchange, code)
		const mapped = this.ourSecurityToShoonyaSecurity.get(ourKey)
		if (mapped) {
			return mapped
		}
		const exch = this.toShoonyaExchange(exchange, segment)
		let tsym = code
		if(exchange === Exchange.NSE) {
			if(segment === Segment.CM) {
				tsym = `${code}${series ? '-'+series : ''}`
			} else if (segment === Segment.FO) {
				const expiryString = epochToDDMMMYY(expiry?.expiry)
				if(categoryCode===SecurityCategory_CategoryCode.FUTURES) {
					tsym = `${exchangeSymbol}${expiryString}F`
				} else if(categoryCode===SecurityCategory_CategoryCode.OPTIONS) {
					const strike = strikePrice / priceMultiplier
					if(optionType === OptionType.CE) {
						tsym = `${exchangeSymbol}${expiryString}C${strike}`
					} else {
						tsym = `${exchangeSymbol}${expiryString}P${strike}`
					}
				}
			}
		} else if(exchange === Exchange.BSE) {
			if(segment === Segment.CM) {
				tsym = exchangeSymbol ?? code
			} else if (segment === Segment.FO) {
				tsym = name ?? code
			}
		}

		return {
			tsym,
			exch
		}
	}

	logout(): void {
		const shoonyaLogoutRequest: ShoonyaLogoutRequest = {
			uid: 'FN134553'
		}
		const jData = JSON.stringify(shoonyaLogoutRequest)
		const payload: ShoonyaApiPayload = {
			jData: jData,
			jKey: localStorage.getItem(PROVIDER_TOKEN) || ''
		}
		thirdPartyApi.post<ShoonyaLogoutResponse>(
			'/NorenWClientTP/Logout',
			payload,
			{
				headers: this.getAuthHeaders()
			}
		)
		localStorage.removeItem(PROVIDER_TOKEN)
		clearAuthSession()
	}

	async placeOrModifyOrder(request: PlaceOrModifyOrdersRequest): Promise<PlaceOrModifyOrdersResponse> {
		const shoonyaOrders = request.order.map(async (order) => {
			const isModifyOrder = !!order.orderId
			const secKey = getSecurityKey(order.security.exchange, order.security.code)
			const security = await getSecurityAsync(secKey)
			const transType = order.transactionType === TransactionType.BUY ? 'B' : 'S'
			
			// Map product type
			let prd = 'C'
			if (order.product === Product.MARGIN) prd = 'M'
			else if (order.product === Product.INTRADAY) prd = 'I'
			else if (order.product === Product.CNC) prd = 'C'
			
			// Map price type (broker order type)
			let prctyp = 'LMT'
			if (order.brokerOrderType === Broker_OrderType.MARKET_ORDER) prctyp = 'MKT'
			else if (order.brokerOrderType === Broker_OrderType.LIMIT_ORDER) prctyp = 'LMT'
			else if (order.brokerOrderType === Broker_OrderType.STOPLOSS_ORDER) prctyp = 'SL-LMT'
			else if (order.brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER) prctyp = 'SL-MKT'
			
			// Map retention/validity
			let ret = 'DAY'
			if (order.validity === Validity.DAY) ret = 'DAY'
			else if (order.validity === Validity.IOC) ret = 'IOC'
			else if (order.validity === Validity.TTL) ret = 'EOS'
			
			const priceMultiplier = security?.priceMultiplier ?? 100
			const priceInRupees = (order.price / priceMultiplier).toFixed(2)
			const triggerPriceInRupees = ((order.triggerPrice ?? 0) / priceMultiplier).toFixed(2)
			const { tsym, exch } = this.ourSecurityToShoonyaSecurity.get(secKey) ?? this.toShoonyaSecurity(security)
			
			const baseOrder = {
				uid: 'FN134553',
				actid: 'FN134553',
				exch: exch,
				tsym: tsym,
				qty: order.quantity.toString(),
				prc: priceInRupees,
				trgprc: triggerPriceInRupees,
				dscqty: order.disclosedQuantity?.toString() || '0',
				prd: prd,
				trantype: transType,
				prctyp: prctyp,
				ret: ret
			}
			
			let shoonyaOrder: ShoonyaPlaceOrderRequest | ShoonyaModifyOrderRequest
			
			// For modify order, add norenordno field
			if (isModifyOrder) {
				shoonyaOrder = {
					...baseOrder,
					norenordno: order.orderId!
				} as ShoonyaModifyOrderRequest
			} else {
				// For place order, add ordersource
				shoonyaOrder = {
					...baseOrder,
					ordersource: 'WEB'
				} as ShoonyaPlaceOrderRequest
			}
			
			// Add optional fields
			if (order.metadata) {
				shoonyaOrder.ext_remarks = order.metadata
			}
			
			return {
				order: shoonyaOrder,
				isModify: isModifyOrder
			}
		})

		const resolvedShoonyaOrders = await Promise.all(shoonyaOrders)

		// Create individual API calls for each order
		const orderPromises = resolvedShoonyaOrders.map(async ({ order: shoonyaOrder, isModify }, index) => {
			const jData = JSON.stringify(shoonyaOrder)
			
			const payload: ShoonyaApiPayload = {
				jData: jData,
				jKey: localStorage.getItem(PROVIDER_TOKEN) || ''
			}
			
			const endpoint = isModify ? '/NorenWClientTP/ModifyOrder' : '/NorenWClientTP/PlaceOrder'
			
			try {
				const response = await thirdPartyApi.post<ShoonyaPlaceOrderResponse | ShoonyaModifyOrderResponse>(
					endpoint,
					payload,
					{
						headers: this.getAuthHeaders()
					}
				)
				
				const responseData = response.data
				
				if (responseData.stat === 'Ok') {
					return {
						orderId: responseData.result || responseData.norenordno || '',
						successful: true,
						commitOrder: true,
						errorMessage: '',
						brokerOrderId: responseData.result || responseData.norenordno
					}
				} else {
					return {
						orderId: '',
						successful: false,
						commitOrder: true,
						errorMessage: responseData.emsg || `Order ${isModify ? 'modification' : 'placement'} failed`
					}
				}
			} catch (error: any) {
				return {
					orderId: '',
					successful: false,
					commitOrder: true,
					errorMessage: error?.response?.data?.emsg || error?.message || `Order ${isModify ? 'modification' : 'placement'} failed`
				}
			}
		})
		const orderResponses = await Promise.all(orderPromises)
		const enrichedOrderResponses = orderResponses.map((response, index) => {
			const originalOrder = request.order[index]
			
			const order: Order = {
				orderDate: Date.now(),
				clientOrderId: originalOrder.clientOrderId || '',
				orderId: response.orderId || '',
				orderType: originalOrder.orderType || OrderType.NORMAL,
				isBasket: originalOrder.isBasket || false,
				basketId: originalOrder.basketId || '',
				batchId: originalOrder.batchId || '',
				brokerOrderType: originalOrder.brokerOrderType || Broker_OrderType.LIMIT_ORDER,
				brokerVariety: originalOrder.brokerVariety || Broker_Variety.REGULAR,
				brokerOrderId: response.brokerOrderId || '',
				security: originalOrder.security,
				product: originalOrder.product || Product.MARGIN,
				transactionType: originalOrder.transactionType || TransactionType.BUY,
				validity: originalOrder.validity || Validity.DAY,
				exchangeOrderId: '',
				quantity: originalOrder.quantity || 0,
				price: originalOrder.price || 0,
				triggerPrice: originalOrder.triggerPrice || 0,
				disclosedQuantity: originalOrder.disclosedQuantity || 0,
				status: response.successful ? OrderStatus.PENDING : OrderStatus.FAILED,
				strategyGroup: originalOrder.strategyGroup || '',
				created: Date.now(),
				exchangeTimestamp: 0,
				message: response.errorMessage || '',
				filledQuantity: 0,
				unfilledQuantity: originalOrder.quantity || 0,
				averageTradedPrice: 0,
				metadata: originalOrder.metadata || '',
				secondaryStatus: 0,
				marketProtection: originalOrder.marketProtection || 0,
				orderExecutionParams: originalOrder.orderExecutionParams,
				orderExecutionMetadata: originalOrder.orderExecutionMetadata,
				parentOrderId: originalOrder.parentOrderId || '',
				immediateExecutePaperOrder: originalOrder.immediateExecutePaperOrder || false
			} as Order
			
			return {
				...response,
				order: order
			}
		})
		
		return {
			orderResponse: enrichedOrderResponses
		} as PlaceOrModifyOrdersResponse
	}

	async getOrders(request: GetAllOrdersRequest): Promise<Order[]> {
		const shoonyaOrderRequest: ShoonyaOrderBookRequest = {
			uid: 'FN134553',
		}
		
		const jData = JSON.stringify(shoonyaOrderRequest)
		
		const payload: ShoonyaApiPayload = {
			jData: jData,
			jKey: localStorage.getItem(PROVIDER_TOKEN) || ''
		}
		
		try {
			const response = await thirdPartyApi.post<ShoonyaOrderBookResponse>(
				'/NorenWClientTP/OrderBook',
				payload,
				{
					headers: this.getAuthHeaders()
				}
			)
			
			const responseData = response.data
			
			if (Array.isArray(responseData)) {
				const orders: Order[] = responseData.map((shoonyaOrder: ShoonyaOrderBookItem) => {
					let product = Product.CNC
					if (shoonyaOrder.prd === 'M') product = Product.MARGIN
					else if (shoonyaOrder.prd === 'I') product = Product.INTRADAY
					else if (shoonyaOrder.prd === 'C') product = Product.CNC
					
					// Map transaction type
					let transactionType = TransactionType.BUY
					if (shoonyaOrder.trantype === 'B') transactionType = TransactionType.BUY
					else if (shoonyaOrder.trantype === 'S') transactionType = TransactionType.SELL
					
					// Map price type to broker order type
					let brokerOrderType = Broker_OrderType.LIMIT_ORDER
					if (shoonyaOrder.prctyp === 'MKT') brokerOrderType = Broker_OrderType.MARKET_ORDER
					else if (shoonyaOrder.prctyp === 'LMT') brokerOrderType = Broker_OrderType.LIMIT_ORDER
					else if (shoonyaOrder.prctyp === 'SL-LMT') brokerOrderType = Broker_OrderType.STOPLOSS_ORDER
					else if (shoonyaOrder.prctyp === 'SL-MKT') brokerOrderType = Broker_OrderType.STOPLOSS_MARKET_ORDER
					
					// Map validity/retention
					let validity = Validity.DAY
					if (shoonyaOrder.ret === 'DAY') validity = Validity.DAY
					else if (shoonyaOrder.ret === 'IOC') validity = Validity.IOC
					else if (shoonyaOrder.ret === 'EOS') validity = Validity.TTL
					
					// Map order status
					let orderStatus = OrderStatus.ORDER_STATUS_UNKNOWN
					const status = shoonyaOrder.status?.toUpperCase()
					if (status === 'OPEN') orderStatus = OrderStatus.OPEN
					else if (status === 'PENDING') orderStatus = OrderStatus.PENDING
					else if (status === 'COMPLETE' || status === 'EXECUTED') orderStatus = OrderStatus.EXECUTED
					else if (status === 'REJECTED') orderStatus = OrderStatus.REJECTED
					else if (status === 'CANCELED') orderStatus = OrderStatus.CANCELLED
					else if (status === 'TRIGGER_PENDING') orderStatus = OrderStatus.TRIGGER_PENDING
					
					
					// Use rejreason for error message if available, otherwise use remarks
					const security = this.shoonyaSecurityToOurSecurity.get(`${shoonyaOrder.exch}:${shoonyaOrder.tsym || ''}`)
					if(!security) return null
					const priceMultiplier = security?.priceMultiplier ?? 100
					
					// Use rejreason for error message if available, otherwise use remarks
					const errorMessage = shoonyaOrder.rejreason || ''
					
					// Convert prices from rupees to paisa (multiply by priceMultiplier)
					const priceInPaisa = parseFloat(shoonyaOrder.prc || '0') * priceMultiplier
					const triggerPriceInPaisa = parseFloat(shoonyaOrder.trgprc || '0') * priceMultiplier
					const avgPriceInPaisa = parseFloat(shoonyaOrder.avgprc || '0') * priceMultiplier
					
					return {
						orderDate: Date.now(),
						clientOrderId: '',
						orderId: shoonyaOrder.norenordno || '',
						orderType: OrderType.NORMAL,
						isBasket: false,
						basketId: '',
						batchId: '',
						brokerOrderType: brokerOrderType,
						brokerVariety: Broker_Variety.REGULAR,
						brokerOrderId: shoonyaOrder.norenordno || '',
						security: {
							exchange: security.exchange,
							code: security.code,
						},
						product: product,
						transactionType: transactionType,
						validity: validity,
						exchangeOrderId: shoonyaOrder.exch_ord_id || '',
						quantity: parseInt(shoonyaOrder.qty || '0'),
						price: priceInPaisa,
						triggerPrice: triggerPriceInPaisa,
						disclosedQuantity: parseInt(shoonyaOrder.dscqty || '0'),
						status: orderStatus,
						strategyGroup: '',
						created: Date.now(),
						exchangeTimestamp: 0,
						message: errorMessage,
						filledQuantity: parseInt(shoonyaOrder.fillshares || '0'),
						unfilledQuantity: parseInt(shoonyaOrder.qty || '0') - parseInt(shoonyaOrder.fillshares || '0'),
						averageTradedPrice: avgPriceInPaisa,
						metadata: shoonyaOrder.remarks || '',
						secondaryStatus: 0,
						marketProtection: 0,
						orderExecutionParams: undefined,
						orderExecutionMetadata: undefined,
						parentOrderId: '',
						immediateExecutePaperOrder: false
					} as Order
				})
				return orders.filter(o=>!!o)
			} else if (responseData.stat === 'Not_Ok') {
				return []
			} else {
				return []
			}
		} catch (error: any) {
			return []
		}
	}

	async cancelOrder(request: CancelOrdersRequest): Promise<CancelOrdersResponse> {
		const state = store.getState()
		const orders = (state.ordersStore?.orders || []) as Order[]
		const orderMap = new Map(orders.map(order => [order.orderId, order]))
		
		const cancelPromises = request.orderId.map(async (orderId, index) => {
			const shoonyaCancelOrder: ShoonyaCancelOrderRequest = {
				norenordno: orderId,
				uid: 'FN134553'
			}
			const jData = JSON.stringify(shoonyaCancelOrder)
			
			const payload: ShoonyaApiPayload = {
				jData: jData,
				jKey: localStorage.getItem(PROVIDER_TOKEN) || ''
			}

			try {
				const response = await thirdPartyApi.post<ShoonyaCancelOrderResponse>(
					'/NorenWClientTP/CancelOrder',
					payload,
					{
						headers: this.getAuthHeaders()
					}
				)

				const responseData = response.data
				
				if (responseData.stat === 'Ok') {
					return {
						orderId: responseData.result || orderId,
						successful: true,
						errorMessage: '',
						brokerOrderId: responseData.result,
						commitOrder: true
					}
				} else {
					return {
						orderId: orderId,
						successful: false,
						commitOrder: true,
						errorMessage: responseData.emsg || 'Order cancellation failed'
					}
				}
			} catch (error: any) {
				return {
					orderId: orderId,
					successful: false,
					errorMessage: error?.response?.data?.emsg || error?.message || 'Order cancellation failed'
				}
			}
		})
		
		const cancelResponses = await Promise.all(cancelPromises)
		
		const enrichedCancelResponses = cancelResponses.map((response) => {
			const existingOrder: Order = orderMap.get(response.orderId)

			const order: Order = existingOrder ? {
				...existingOrder,
				status: response.successful ? OrderStatus.CANCELLED : existingOrder.status,
				message: response.errorMessage || existingOrder.message
			} : {
				orderDate: Date.now(),
				clientOrderId: '',
				orderId: response.orderId,
				orderType: OrderType.NORMAL,
				isBasket: false,
				basketId: '',
				batchId: '',
				brokerOrderType: Broker_OrderType.LIMIT_ORDER,
				brokerVariety: Broker_Variety.REGULAR,
				brokerOrderId: response.brokerOrderId || '',
				security: { exchange: Exchange.NSE, code: '' },
				product: Product.MARGIN,
				transactionType: TransactionType.BUY,
				validity: Validity.DAY,
				exchangeOrderId: '',
				quantity: 0,
				price: 0,
				triggerPrice: 0,
				disclosedQuantity: 0,
				status: response.successful ? OrderStatus.CANCELLED : OrderStatus.FAILED,
				strategyGroup: '',
				created: Date.now(),
				exchangeTimestamp: 0,
				message: response.errorMessage || '',
				filledQuantity: 0,
				unfilledQuantity: 0,
				averageTradedPrice: 0,
				metadata: '',
				secondaryStatus: 0,
				marketProtection: 0,
				orderExecutionParams: undefined,
				orderExecutionMetadata: undefined,
				parentOrderId: '',
				immediateExecutePaperOrder: false
			} as Order
			
			return {
				...response,
				order: order
			}
		})
		
		return {
			orderResponse: enrichedCancelResponses
		} as CancelOrdersResponse
	}

	async getPositions(request: PositionRequest): Promise<PositionResponse> {
		const shoonyaPositionRequest: ShoonyaPositionBookRequest = {
			uid: 'FN134553',
			actid: 'FN134553'
		}
		
		const jData = JSON.stringify(shoonyaPositionRequest)
		
		const payload: ShoonyaApiPayload = {
			jData: jData,
			jKey: localStorage.getItem(PROVIDER_TOKEN) || ''
		}

		try {
			const response = await thirdPartyApi.post<ShoonyaPositionBookResponse>(
				'/NorenWClientTP/PositionBook',
				payload,
				{
					headers: this.getAuthHeaders()
				}
			)
			const responseData = response.data
			if (Array.isArray(responseData)) {
				const positions: Position[] = responseData.map((shoonyaPosition: ShoonyaPositionBookItem) => {
					let product = Product.CNC
					if (shoonyaPosition.prd === 'M') product = Product.MARGIN
					else if (shoonyaPosition.prd === 'I') product = Product.INTRADAY
					else if (shoonyaPosition.prd === 'C') product = Product.CNC
					
					// Calculate quantities
					const netQty = parseInt(shoonyaPosition.netqty || '0')
					const dayBuyQty = parseInt(shoonyaPosition.daybuyqty || '0')
					const daySellQty = parseInt(shoonyaPosition.daysellqty || '0')
					
					// Calculate day net quantity (positive for buy, negative for sell)
					const dayNetQty = dayBuyQty - daySellQty
					
					// closePosition is the net open position (positive for long, negative for short)
					const closePosition = netQty
					
					// Calculate closePositionValue
					// For buy positions: use day buy average price
					// For sell positions: use day sell average price (negative)
					const dayBuyAvgPrice = parseFloat(shoonyaPosition.daybuyavgprc || '0')
					const daySellAvgPrice = parseFloat(shoonyaPosition.daysellavgprc || '0')
					
					let closePositionValue = 0
					if (dayBuyQty > 0 && daySellQty === 0) {
						// Pure buy position
						closePositionValue = dayBuyAvgPrice * dayBuyQty
					} else if (daySellQty > 0 && dayBuyQty === 0) {
						// Pure sell position (negative value)
						closePositionValue = -(daySellAvgPrice * daySellQty)
					} else if (dayBuyQty > 0 && daySellQty > 0) {
						// Mixed position - calculate based on net
						if (netQty > 0) {
							// Net long position
							closePositionValue = dayBuyAvgPrice * netQty
						} else if (netQty < 0) {
							// Net short position
							closePositionValue = -(daySellAvgPrice * Math.abs(netQty))
						}
					}

					// Convert Shoonya security to our format
					const security = this.shoonyaSecurityToOurSecurity.get(`${shoonyaPosition.exch}:${shoonyaPosition.tsym || ''}`)
					if(!security) return null
					closePositionValue *= (security?.priceMultiplier??100)
					
					
					// Calculate overnight position
					const overnightPosition = netQty - dayNetQty
					
					// Calculate realised quantities and values
					const realisedQuantity = Math.min(dayBuyQty, daySellQty)
					const realisedBuyValue = dayBuyAvgPrice * realisedQuantity
					const realisedSellValue = daySellAvgPrice * realisedQuantity
					
					return {
						basketId: `default_${uuid()}_${productToJSON(product)}_${security.code}_${shoonyaPosition.exch}`,
						product: product,
						security: {
							exchange: security.exchange,
							code: security.code
						},
						isBasket: false,
						strategyGroup: '',
						date: Date.now(),
						overnightPosition: overnightPosition,
						overnightPositionValue: 0,
						closePosition: closePosition,
						closePositionValue: closePositionValue,
						dayBuyQuantity: dayBuyQty,
						daySellQuantity: daySellQty,
						dayBuyValue: parseFloat(shoonyaPosition.daybuyamt || '0'),
						daySellValue: parseFloat(shoonyaPosition.daysellamt || '0'),
						realisedQuantity: realisedQuantity,
						realisedBuyValue: realisedBuyValue,
						realisedSellValue: realisedSellValue,
						realisedProfit: parseFloat(shoonyaPosition.rpnl || '0'),
						status: netQty !== 0 ? Position_Status.ACTIVE : Position_Status.CLOSED,
						adjusted: false
					}
				})
				
				return {
					position: positions.filter(p=>!!p)
				} as PositionResponse
			} else if (responseData.stat === 'Not_Ok') {
				return {
					position: []
				} as PositionResponse
			} else {
				return {
					position: []
				} as PositionResponse
			}
		} catch (error: any) {
			return {
				position: []
			} as PositionResponse
		}
	}

	async getPlacedOrder(params: any): Promise<Order> {
		return {
			orderId: params.orderId || 'MOCK_ORDER',
			status: OrderStatus.PENDING
		} as Order
	}

	async getHoldings(accountId: string | undefined): Promise<Holding[]> {
		const shoonyaHoldingsRequest: ShoonyaHoldingsRequest = {
			uid: 'FN134553',
			actid: 'FN134553',
			prd: 'C'
		}
		const jData = JSON.stringify(shoonyaHoldingsRequest)
		const payload: ShoonyaApiPayload = {
			jData: jData,
			jKey: localStorage.getItem(PROVIDER_TOKEN) || ''
		}
		try {
			const response = await thirdPartyApi.post<ShoonyaHoldingsResponse>(
				'/NorenWClientTP/Holdings',
				payload,
				{
					headers: this.getAuthHeaders()
				}
			)
			const responseData = response.data
			if (Array.isArray(responseData)) {
				// Transform Shoonya holdings to our format
				const holdings: Holding[] = responseData.map((shoonyaHolding: ShoonyaHoldingItem) => {
					// Get the first exchange/symbol info (holdings can have multiple exchanges)
					const exchTsym = shoonyaHolding.exch_tsym?.[0]
					
					if (!exchTsym) {
						return null
					}
					
					const security = this.shoonyaSecurityToOurSecurity.get(`${exchTsym.exch}:${exchTsym.tsym || ''}`)
					
					if(!security) return null
					// Parse quantities
					const quantity = parseInt(shoonyaHolding.holdqty || '0')
					const t1Quantity = parseInt(shoonyaHolding.npoadqty || '0')
					
					const averageBuyPrice = parseFloat(shoonyaHolding.upldprc || '0') * (security.priceMultiplier??100)
					
					return {
						security: {
							exchange: security.exchange,
							code: security.code
						},
						quantity: quantity,
						averageBuyPrice: averageBuyPrice,
						t1Quantity: t1Quantity
					} as Holding
				}).filter(holding => holding !== null) as Holding[]
				return holdings.filter(h=>!!h)
			} else if (responseData.stat === 'Not_Ok') {
				return []
			} else {
				return []
			}
		} catch (error: any) {
			return []
		}
	}

	async getLoginUrl(body: any): Promise<AuthProviderLoginUrlResponse> {
		const response = await api.post(
			`/auth/login-url`,
			JSON.stringify(body),
			{
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/x-protobuf'
				},
				responseType: 'arraybuffer'
			}
		)

		return AuthProviderLoginUrlResponse.decode(new Uint8Array(response.data))
	}

	async loginWithMail(body: LoginRequest): Promise<LoginResponse> {
		return {
			accessToken: 'MOCK_TOKEN_' + Date.now(),
			tokenType: 'Bearer',
			providerToken: 'MOCK_PROVIDER_TOKEN',
			refreshToken: 'MOCK_REFRESH_TOKEN',
			user: {
				id: 'MOCK_USER_ID',
				email: body.email,
				name: 'Mock User'
			}
		} as LoginResponse
	}

	async loginWithMobile(_body: LoginWithMobileRequest): Promise<LoginWithMobileResponse> {
		return { requestId: 'MOCK_REQUEST_ID' }
	}

	async verifyMobileLogin(_body: VerifyMobileLoginRequest): Promise<LoginResponse> {
		return {
			accessToken: 'MOCK_TOKEN_' + Date.now(),
			tokenType: 'Bearer'
		} as LoginResponse
	}

	async signUpWithMail(userSignUpRequest: UserSignUpRequest): Promise<LoginResponse> {
		return {
			accessToken: 'MOCK_TOKEN_' + Date.now(),
			tokenType: 'Bearer',
			isNewUser: false
		} as LoginResponse
	}

	async sendEmailOtp(_email: string, _options?: { purpose?: string }): Promise<any> {
		return {
			data: {
				success: true,
				message: 'OTP sent successfully'
			}
		}
	}

	async forgetPassword(_body: ForgetPasswordRequest): Promise<User> {
		return {} as User
	}

	async syncTradingAccountMargin(request: GetTradingAccountMarginRequest): Promise<TradingAccountMargin> {
		const shoonyaLimitsRequest: ShoonyaLimitsRequest = {
			uid: 'FN134553',
			actid: 'FN134553'
		}
		const jData = JSON.stringify(shoonyaLimitsRequest)
		const payload: ShoonyaApiPayload = {
			jData: jData,
			jKey: localStorage.getItem(PROVIDER_TOKEN) || ''
		}
		try {
			const response = await thirdPartyApi.post<ShoonyaLimitsResponse>(
				'/NorenWClientTP/Limits',
				payload,
				{
					headers: this.getAuthHeaders()
				}
			)
			const responseData = response.data
			if (responseData.stat === 'Ok') {
				const cashAvailable = parseFloat(responseData.cash || '0')
				const marginUsed = parseFloat(responseData.marginused || '0')
				const marginAvailable = cashAvailable - marginUsed
				return {
					accountId: request.accountId || '',
					marginAvailable: marginAvailable,
					marginUsed: marginUsed,
					cashAvailable: cashAvailable
				} as TradingAccountMargin
			} else {
				return {
					accountId: request.accountId || '',
					marginAvailable: 0,
					marginUsed: 0,
					cashAvailable: 0
				} as TradingAccountMargin
			}
		} catch (error: any) {
			return {
				accountId: request.accountId || '',
				marginAvailable: 0,
				marginUsed: 0,
				cashAvailable: 0
			} as TradingAccountMargin
		}
	}

	async getRequiredMargin(request: MarginFromBrokerRequest): Promise<MarginFromBrokerResponse> {
		if (!request.orders || request.orders.length === 0) {
			return {
				totalMargin: 0,
				brokerCharges: 0
			} as MarginFromBrokerResponse
		}
		
		try {
			const marginPromises = request.orders.map(async (order) => {
				const security = order.security
				if (!security) {
					return { margin: 0, charges: 0 }
				}
				
				const secKey = getSecurityKey(security.exchange, security.code)
				const fullSecurity = await getSecurityAsync(secKey)
				
				// Map transaction type
				const transType = order.transactionType === TransactionType.BUY ? 'B' : 'S'
				
				// Map product type
				let prd = 'C'
				if (order.product === Product.MARGIN) prd = 'M'
				else if (order.product === Product.INTRADAY) prd = 'I'
				else if (order.product === Product.CNC) prd = 'C'
				
				// Map price type (broker order type)
				let prctyp = 'LMT'
				if (order.orderType === Broker_OrderType.MARKET_ORDER) prctyp = 'MKT'
				else if (order.orderType === Broker_OrderType.LIMIT_ORDER) prctyp = 'LMT'
				else if (order.orderType === Broker_OrderType.STOPLOSS_ORDER) prctyp = 'SL-LMT'
				else if (order.orderType === Broker_OrderType.STOPLOSS_MARKET_ORDER) prctyp = 'SL-MKT'
				
				const priceMultiplier = fullSecurity?.priceMultiplier ?? 100
				const priceInRupees = (order.price / priceMultiplier).toFixed(2)
				const triggerPriceInRupees = ((order.triggerPrice ?? 0) / priceMultiplier).toFixed(2)
				
				const { tsym, exch } = this.toShoonyaSecurity(fullSecurity || security)
				
				const shoonyaMarginRequest: ShoonyaOrderMarginRequest = {
					uid: 'FN134553',
					actid: 'FN134553',
					exch: exch,
					tsym: tsym,
					qty: order.position.toString(),
					prc: priceInRupees,
					trgprc: triggerPriceInRupees,
					prd: prd,
					trantype: transType,
					prctyp: prctyp
				}
				
				const jData = JSON.stringify(shoonyaMarginRequest)
				
				const payload: ShoonyaApiPayload = {
					jData: jData,
					jKey: localStorage.getItem(PROVIDER_TOKEN) || ''
				}
				
				try {
					const response = await thirdPartyApi.post<ShoonyaOrderMarginResponse>(
						'/NorenWClientTP/GetOrderMargin',
						payload,
						{
							headers: this.getAuthHeaders()
						}
					)
					
					const responseData = response.data
					
					if (responseData.stat === 'Ok') {
						const orderMargin = parseFloat(responseData.ordermargin || '0')
						return { margin: orderMargin, charges: 0 }
					} else {
						return { margin: 0, charges: 0 }
					}
				} catch (error: any) {
					return { margin: 0, charges: 0 }
				}
			})
			const marginResults = await Promise.all(marginPromises)
			const totalMargin = marginResults.reduce((sum, result) => sum + result.margin, 0)
			const totalCharges = marginResults.reduce((sum, result) => sum + result.charges, 0)
			
			return {
				totalMargin: totalMargin,
				brokerCharges: totalCharges
			} as MarginFromBrokerResponse
		} catch (error: any) {
			return {
				totalMargin: 0,
				brokerCharges: 0
			} as MarginFromBrokerResponse
		}
	}

	async validateToken(body: ValidateThirdPartyTokenRequest): Promise<LoginResponse> {
		const response = await rawApi.post(
			`/auth/oauth/token/validate`,
			JSON.stringify(body),
			{
				headers: {
					'Content-Type': 'application/json',
					'Accept': 'application/json'
				},
				responseType: 'json'
			}
		)

        return response.data as LoginResponse
	}

	async getActiveSessions(): Promise<GetActiveSessionsResponse> {
		return { sessions: [] }
	}

	async revokeSession(_deviceId: string): Promise<OperationResponse> {
		return { success: true, message: '' }
	}
}
